import { useMemo } from 'react';
import { css } from 'styled-components';
import { lightTheme } from '../../themes/index.js';

function getBoxOrSizeCssOutput(value, order = 'top right bottom left') {
    if (typeof value === 'string') {
        return value;
    }
    let { top = '0px', bottom = '0px', left = '0px', right = '0px' } = value;
    const { horizontal, vertical } = value;
    if (horizontal) {
        left = horizontal;
        right = horizontal;
    }
    if (vertical) {
        top = vertical;
        bottom = vertical;
    }
    return order
        .replace('top', top)
        .replace('bottom', bottom)
        .replace('left', left)
        .replace('right', right);
}
function getBorderCssOutput(value, side, theme) {
    if (typeof value === 'string') {
        return value;
    }
    function buildCss(border) {
        const { width = '1px', color = 'accent1', style = 'solid' } = border;
        return `${width} ${style} ${theme.colors.background[color]}`;
    }
    if ('width' in value || 'style' in value || 'color' in value) {
        return buildCss(value);
    }
    const sidedValue = value;
    if (side in value && !!sidedValue[side]) {
        return buildCss(value[side]);
    }
    if ((side === 'left' || side === 'right') && !!sidedValue.horizontal) {
        return buildCss(sidedValue.horizontal);
    }
    if ((side === 'top' || side === 'bottom') && !!sidedValue.vertical) {
        return buildCss(sidedValue.vertical);
    }
    return undefined;
}
const themeBreakpoints = Object.keys(lightTheme.media);
const themeBreakpointsAndDefault = [
    'default',
    ...themeBreakpoints,
];
/**
 * Map Crate "component props", which may or may not be specified with breakpoints,
 * to "styled props", which *always* have breakpoints. If the component props do not
 * specify breakpoints, this sets the value as the `{ default: ... }` styled props breakpoint.
 *
 * This hook also renames all of the prop keys to be prefixed with a dollar sign (`$`),
 * this tells styled-components to treat it as a 'transient prop', which means the props
 * do not leak into the actual DOM nodes.
 */
function useMapComponentPropsToStyledProps(componentProps) {
    const styledProps = useMemo(() => {
        const keys = Object.keys(componentProps);
        return keys.reduce((acc, key) => {
            // This prefixes the key with a dollar sign and enforces to Typescript
            // that the key is acceptable later on, because Object.keys returns a `string[]` type.
            const transientKey = `$${key}`;
            // This value could be an object with breakpoints with the value type
            // specified by the 'StyleControls' genneric, or just the value type.
            const styleValue = componentProps[key];
            // A hackish "type guard" to check if the `styleValue` variable contains breakpoints
            // If it does, we can return the value unmodified but with the new transient key.
            if (styleValue &&
                typeof styleValue === 'object' &&
                !!Object.keys(styleValue).find(subKey => themeBreakpointsAndDefault.includes(subKey))) {
                return Object.assign(Object.assign({}, acc), { [transientKey]: styleValue });
            }
            // If the `styleValue` has no breakpoints, create a 'StyleAttributeDefaultAndMedia'
            // container that just has the 'default' breakpoint key and reuse the same style value.
            return Object.assign(Object.assign({}, acc), { [transientKey]: { default: styleValue } });
        }, {});
    }, [componentProps]);
    return styledProps;
}
/**
 * This function generates the CSS used by the crate component.
 *
 * It accepts "style props", which means they have transient prop key names,
 * and all values are nested within breakpoint keys.
 *
 * "constructors" are functions that are given data and return css strings based
 * on that data. The key name is the css proeprty key, and output of the function
 * is the css property value.
 *
 * Constructor functions are also given some utility data and functions, as
 * defined in 'ConstructorStyleFunction'.
 */
function constructCss(styledProps, constructors) {
    const previousCssValue = {};
    let stylesheet = [];
    // NOTE: If this gets slow at 'scale', we could memoize this so there's less repeat computation
    //       the custom getter would be able to infer what the dependencies are for future construct() calls
    function internalConstructLoop(breakpoint) {
        const result = [];
        Object.keys(constructors).forEach(cssProperty => {
            // This is a getter function passed to each consumer.
            // This function is responsible for safely accessing the styled props and checking
            // if a value exists for the current breakpoint being iterated over.
            //
            // This function also ensures that the returned style value is typed based on
            // how it's defined in the 'StyleControls' generic, which means there is no
            // surrounding breakpoint structure, and the access pattern within the CSS
            // generator is quite simple.
            //
            // This function is scoped within 'constructCss' to take advantage of the type generics
            // already being passed to 'constructCss', so it keeps the Typescript a bit smaller.
            function getStyledPropValue(key) {
                const typeSafeKey = `$${String(key)}`;
                if (!(typeSafeKey in styledProps)) {
                    return undefined;
                }
                // Technically this is the only 'unsafe' cast going on, because the type of
                // the 'StyledProps' generic is unknown. But this is just an internal function
                // called in a select few places and I'm not that concerned about mis-use.
                const attributeWithMedia = styledProps[typeSafeKey];
                return attributeWithMedia[breakpoint] || undefined;
            }
            const cssValue = constructors[cssProperty]({
                styledProps,
                breakpoint,
                getStyledPropValue,
                theme: styledProps.theme,
            });
            // Check if the returned value is a valid string and it does not match CSS
            // that was already added to the stylesheet (no need to add duplicate styling).
            if (!!cssValue &&
                previousCssValue[cssProperty] !== cssValue &&
                typeof cssValue === 'string') {
                previousCssValue[cssProperty] = cssValue;
                result.push(`${cssProperty}: ${cssValue};`);
            }
        });
        return result;
    }
    stylesheet = internalConstructLoop('default');
    themeBreakpoints.forEach(breakpoint => {
        const breakpointStyles = internalConstructLoop(breakpoint);
        if (breakpointStyles.length) {
            stylesheet = [
                ...stylesheet,
                `${lightTheme.media[breakpoint]}{`,
                ...breakpointStyles,
                '}',
            ];
        }
    });
    return css `
    ${stylesheet.join('')}
  `;
}

export { constructCss, getBorderCssOutput, getBoxOrSizeCssOutput, useMapComponentPropsToStyledProps };
